import { DUTY_FREE_OTHER_FACILITY_CLIENT_CODES, LOTTE_CATEGORY_FIELD } from '@/constants/lotte-api/category';
import { isDutyFreePoi } from '@/utils/duty-free';
import { compareByKioskBuilding, type KioskPriorityBuilding } from '@/utils/kiosk-building-priority';
import type { BuildingCategory, BuildingInStoreInfo, FloorInfo, PoiContent } from '@/services/dto';
import type { PoiContentSlide } from '@/types/shop';
import { disassembleCompleteCharacter } from 'es-hangul';
import { extractConsonants } from './hangul/extractConsonants';
import { sortKoEnFirst } from './sort/ko-en-first/sortKoEnFirst';

export const parseFloorLabel = (label: string): number => {
    const match = label.match(/(B?)(\d+)/i);
    return match ? (match[1].toUpperCase() === 'B' ? -parseInt(match[2], 10) : parseInt(match[2], 10)) : 0;
};

const extractChosung = (str: string): string => {
    let result = '';
    for (const char of str) {
        const disassembled = disassembleCompleteCharacter(char);
        if (disassembled) {
            // Only take choseong (first consonant), not all consonants
            result += disassembled.choseong;
        } else {
            // For non-Hangul characters, keep as is
            result += char;
        }
    }
    return result;
};

/**
 * @desc 매장 검색에서 매장 필터링 시 사용하는 로직 (chosung 기반 검색)
 * @param {string} targetText 검색 대상 텍스트
 * @param {string} searchText 검색어
 * @returns {boolean} 검색 결과 - 모음만 입력시 false 반환
 */
export const searchByText = (targetText: string, searchText: string): boolean => {
    if (!searchText.trim()) return true;

    const target = targetText.toUpperCase();
    const search = searchText.toUpperCase();

    // Non-Hangul queries: simple substring match
    if (!/[ㄱ-ㅎㅏ-ㅣ가-힣]/.test(search)) {
        return target.includes(search);
    }

    // Korean search logic
    // Check if searchText contains only chosung characters
    const isSearchOnlyChosung = /^[ㄱ-ㅎ]+$/.test(search);

    const targetChosung = isSearchOnlyChosung ? extractChosung(target) : target;
    return targetChosung.includes(search);
};

/**
 * @desc 검색 결과를 정렬하는 함수 (한국어 초성 우선 정렬)
 * @param {Array} items 정렬할 아이템 배열
 * @param {string} searchText 검색어
 * @param {string} nameField 이름 필드명
 * @param {string} lang 언어 코드
 * @returns {Array} 정렬된 아이템 배열
 */
export const sortSearchResults = <T extends Record<string, unknown>>(items: T[], searchText: string, nameField: keyof T, lang: string = 'ko'): T[] => {
    if (!searchText.trim()) {
        return items.sort((a: T, b: T) => {
            const nameA = (a[nameField] as Record<string, string>)?.[lang] || '';
            const nameB = (b[nameField] as Record<string, string>)?.[lang] || '';
            return sortKoEnFirst(nameA.toUpperCase(), nameB.toUpperCase());
        });
    }

    const search = searchText.toUpperCase();
    const isKorean = /[ㄱ-ㅎㅏ-ㅣ가-힣]/.test(search);

    return items.sort((a: T, b: T): number => {
        const nameA = (a[nameField] as Record<string, string>)?.[lang] || '';
        const nameB = (b[nameField] as Record<string, string>)?.[lang] || '';

        if (isKorean) {
            // 한국어인 경우 초성으로 시작하는지 확인
            const disassembleString = (str: string): string => {
                let result = '';
                for (const char of str) {
                    const disassembled = disassembleCompleteCharacter(char);
                    if (disassembled) {
                        result += disassembled.choseong + disassembled.jungseong + disassembled.jongseong;
                    } else {
                        result += char;
                    }
                }
                return result;
            };

            const startsWithChosungA = extractConsonants(disassembleString(nameA)).startsWith(search);
            const startsWithChosungB = extractConsonants(disassembleString(nameB)).startsWith(search);

            // 검색어가 포함된 아이템들을 오름차순 정렬
            if (startsWithChosungA && startsWithChosungB) {
                return sortKoEnFirst(nameA.toUpperCase(), nameB.toUpperCase());
            }

            if (startsWithChosungA) return -1;
            if (startsWithChosungB) return 1;

            return sortKoEnFirst(nameA.toUpperCase(), nameB.toUpperCase());
        } else {
            // 한국어가 아닌 경우 일반 문자열 검색
            const startsWithSearchWordA = nameA.toUpperCase().startsWith(search);
            const startsWithSearchWordB = nameB.toUpperCase().startsWith(search);

            if (startsWithSearchWordA && startsWithSearchWordB) {
                return sortKoEnFirst(nameA.toUpperCase(), nameB.toUpperCase());
            }

            if (startsWithSearchWordA) return -1;
            if (startsWithSearchWordB) return 1;

            return sortKoEnFirst(nameA.toUpperCase(), nameB.toUpperCase());
        }
    });
};

export const getFloorName = (lang: string, studioFloorId: string, floors: FloorInfo[]) => {
    const floorInfo = floors.find((floor) => floor.floorCode === studioFloorId);
    return floorInfo?.name?.[lang] || '';
};

export const getBuildingNameByStudioId = (lang: string, studioBuildingId: string, buildings: BuildingInStoreInfo[]) => {
    const building = buildings.find((building) => building.studioBuildingId === studioBuildingId);
    return building?.name?.[lang] || '';
};

export const getBuildingNameByBuildingId = (lang: string, buildingId: string, buildings: BuildingInStoreInfo[]) => {
    const building = buildings.find((building) => building.id === buildingId);
    return building?.name?.[lang] || '';
};

/** Strip storeCode suffix from `studioBuildingId_storeCode` (or trailing `_` when cstrCd is empty). */
export const getStudioBuildingIdFromMapId = (buildingMapId?: string | null): string => {
    if (!buildingMapId) return '';
    return buildingMapId.replace(/_.*$/, '');
};

export const toBuildingMapId = (studioBuildingId: string, storeCode: string): string => {
    return studioBuildingId.includes('_') ? studioBuildingId : `${studioBuildingId}_${storeCode}`;
};

/**
 * Resolve config building from map building id.
 * Falls back to studioBuildingId match when cstrCd was empty (`uuid_` vs `uuid_0001`).
 */
export const findBuildingByMapId = (
    buildings: BuildingInStoreInfo[] | undefined,
    buildingMapId?: string | null
): BuildingInStoreInfo | undefined => {
    if (!Array.isArray(buildings) || !buildingMapId) return undefined;

    const exact = buildings.find(({ studioBuildingId, storeCode }) =>
        studioBuildingId?.includes('_') ? studioBuildingId === buildingMapId : `${studioBuildingId}_${storeCode}` === buildingMapId
    );
    if (exact) return exact;

    const studioId = getStudioBuildingIdFromMapId(buildingMapId);
    if (!studioId) return undefined;

    return buildings.find(({ studioBuildingId }) => {
        if (!studioBuildingId) return false;
        const configStudioId = studioBuildingId.includes('_') ? getStudioBuildingIdFromMapId(studioBuildingId) : studioBuildingId;
        return configStudioId === studioId;
    });
};

/**
 * Build `studioBuildingId_storeCode` for floor-guide.
 * Duty-free POIs often have empty cstrCd — fall back to config building storeCode.
 */
export const getBuildingMapId = (poi: PoiContent, buildings?: BuildingInStoreInfo[]): string => {
    const studioBuildingId = poi.studioBuildingId || '';
    const cstrCd = poi.contents?.cstrCd?.ko || '';
    if (cstrCd) {
        return toBuildingMapId(studioBuildingId, cstrCd);
    }

    const matched = findBuildingByMapId(buildings, studioBuildingId) ?? findBuildingByMapId(buildings, `${studioBuildingId}_`);
    if (matched) {
        return toBuildingMapId(matched.studioBuildingId, matched.storeCode);
    }

    return `${studioBuildingId}_`;
};

export const getSortedContents = (
    contents: PoiContentSlide[],
    language: string,
    priorityBuilding: KioskPriorityBuilding | null = null,
): PoiContentSlide[] => {
    return contents.sort((a, b) => {
        const priorityDiff = compareByKioskBuilding(a, b, priorityBuilding);
        if (priorityDiff !== 0) return priorityDiff;

        const nameA = a.data.name[language] || '';
        const nameB = b.data.name[language] || '';
        return nameA.localeCompare(nameB, language, {
            numeric: false,
            sensitivity: 'base',
            ignorePunctuation: true,
        });
    });
};

const matchesBuilding = (poi: PoiContent, building: BuildingInStoreInfo) =>
    poi?.contents?.cstrCd?.ko === building.storeCode && poi?.contents?.cstrTownCd?.ko === building.buildingCode;

export const filterPoiContents = (building: BuildingInStoreInfo, poiContents: PoiContent[]) => {
    const poiContentsByBuilding = poiContents?.filter((poi) => matchesBuilding(poi, building));
    const shopPoiSource = poiContents?.filter((poi) => matchesBuilding(poi, building) || isDutyFreePoi(poi)) ?? [];

    const shopPoiContents: PoiContent[] = [];
    const fnbPoiContents: PoiContent[] = [];
    const facilityPoiContents: PoiContent[] = [];
    const otherUtilityPoiContents: PoiContent[] = [];

    // Lấy categories từ building được chỉ định để làm tiêu chí filter
    const rootShopCategory = building.buildingCategories.find((category) => category.clientCode === LOTTE_CATEGORY_FIELD.LARGE_CLASSIFICATION_CODE);
    const rootFnbCategory = building.buildingCategories.find((category) => category.clientCode === LOTTE_CATEGORY_FIELD.SMALL_CLASSIFICATION_CODE);
    const rootFacilityCategory = building.buildingCategories.find((category) => category.clientCode === LOTTE_CATEGORY_FIELD.MEDIUM_CLASSIFICATION_CODE);
    const rootOtherUtilityCategory = building.buildingCategories.find((category) => category.clientCode === LOTTE_CATEGORY_FIELD.OTHER_FACILITY_CODE);

    shopPoiSource.forEach((poi: PoiContent) => {
        if (isDutyFreePoi(poi)) {
            shopPoiContents.push(poi);
            return;
        }

        const categoriesSet = new Set(poi.rootCategories ?? []);
        const isShopContent = categoriesSet.has(rootShopCategory?.categoryCode as string) || categoriesSet.has(rootFacilityCategory?.categoryCode as string);

        if (isShopContent) shopPoiContents.push(poi);
    });

    // Filter POI contents từ tất cả buildings dựa trên categories của building được chỉ định
    poiContentsByBuilding?.forEach((poi: PoiContent) => {
        const categoriesSet = new Set(poi.rootCategories ?? []);

        const isFnbContent = categoriesSet.has(rootFnbCategory?.categoryCode as string);
        const isFacilityContent = categoriesSet.has(rootFacilityCategory?.categoryCode as string);
        const isOtherUtilityContent = categoriesSet.has(rootOtherUtilityCategory?.categoryCode as string);

        if (isFacilityContent) facilityPoiContents.push(poi);
        if (isFnbContent) fnbPoiContents.push(poi);
        if (isOtherUtilityContent) otherUtilityPoiContents.push(poi);
    });

    return {
        shopPoiContents,
        fnbPoiContents,
        facilityPoiContents,
        otherUtilityPoiContents,
    };
};

const filterCategories = (building: BuildingInStoreInfo, poiContents: PoiContent[]) => {
    const shopMap = new Map<string, BuildingCategory>();
    const fnbMap = new Map<string, BuildingCategory>();
    const facilityMap = new Map<string, BuildingCategory>();
    const otherFacilityMap = new Map<string, BuildingCategory>();

    const createKey = (categoryCode: string, clientCode: string) => `${categoryCode}:${clientCode}`;

    const result = {
        shopRootCategory: null as BuildingCategory | null,
        fnbRootCategory: null as BuildingCategory | null,
        facilityRootCategory: null as BuildingCategory | null,
        otherFacilityRootCategory: null as BuildingCategory | null,
    };

    const categoriesWithContent = new Set<string>();
    const dutyFreeCategoriesWithContent = new Set<string>();

    poiContents.forEach((poi) => {
        poi.categories.forEach((code) => categoriesWithContent.add(code));
        if (!isDutyFreePoi(poi)) return;
        poi.categories.forEach((code) => dutyFreeCategoriesWithContent.add(code));
    });

    building.buildingCategories.forEach((category) => {
        switch (category.clientCode) {
            case LOTTE_CATEGORY_FIELD.LARGE_CLASSIFICATION_CODE:
                result.shopRootCategory = category;
                break;
            case LOTTE_CATEGORY_FIELD.SMALL_CLASSIFICATION_CODE:
                result.fnbRootCategory = category;
                break;
            case LOTTE_CATEGORY_FIELD.MEDIUM_CLASSIFICATION_CODE:
                result.facilityRootCategory = category;
                // Vì dữ liệu api trả về trong building category (loại cửa hàng) không có category tiện ích giống như fnb (fnb có dữ liệu category thuộc shop)
                // nên phải thêm root category của facility vào shop category để tìm kiếm
                shopMap.set(createKey(category.categoryCode, category.clientCode), category);
                break;
            case LOTTE_CATEGORY_FIELD.OTHER_FACILITY_CODE:
                result.otherFacilityRootCategory = category;
                break;
        }

        const key = createKey(category.categoryCode, category.clientCode);

        const isDutyFreeOtherFacility =
            category.parentClientCode === LOTTE_CATEGORY_FIELD.DUTY_FREE_OTHER_FACILITY_CODE &&
            DUTY_FREE_OTHER_FACILITY_CLIENT_CODES.has(category.clientCode);

        if (isDutyFreeOtherFacility) {
            if (dutyFreeCategoriesWithContent.has(category.categoryCode)) {
                otherFacilityMap.set(key, category);
            }
            return;
        }

        if (!categoriesWithContent.has(category.categoryCode)) return;

        switch (category.parentClientCode) {
            case LOTTE_CATEGORY_FIELD.LARGE_CLASSIFICATION_CODE:
                shopMap.set(key, category);
                break;
            case LOTTE_CATEGORY_FIELD.SMALL_CLASSIFICATION_CODE:
                fnbMap.set(key, category);
                break;
            case LOTTE_CATEGORY_FIELD.MEDIUM_CLASSIFICATION_CODE:
                facilityMap.set(key, category);
                break;
            case LOTTE_CATEGORY_FIELD.OTHER_FACILITY_CODE:
                otherFacilityMap.set(key, category);
                break;
        }
    });

    return {
        ...result,
        shopCategories: [...shopMap.values()],
        fnbCategories: [...fnbMap.values()],
        facilityCategories: [...facilityMap.values()],
        otherFacilityCategories: [...otherFacilityMap.values()],
    };
};

export const extractMatchingKeyword = (crltSrchKwrd: Record<string, string>, searchText: string, language: string): string | undefined => {
    if (!searchText?.trim()) return undefined;

    const normalizedSearchText = searchText.trim();

    const hashTags = crltSrchKwrd[language] || '';
    const keywords = hashTags.split(',').map((k) => k.trim());

    for (const keyword of keywords) {
        if (keyword && searchByText(keyword, normalizedSearchText)) {
            return keyword;
        }
    }

    const keywordDefault = crltSrchKwrd['ko'] || '';
    const keywordsDefault = keywordDefault.split(',').map((k) => k.trim());

    for (const keyword of keywordsDefault) {
        if (keyword && searchByText(keyword, normalizedSearchText)) {
            return keyword;
        }
    }

    return undefined;
};

const poiUtils = {
    parseFloorLabel,
    searchByText,
    sortSearchResults,
    getFloorName,
    getBuildingNameByStudioId,
    getSortedContents,
    filterPoiContents,
    filterCategories,
    extractMatchingKeyword,
};

export default poiUtils;
